import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { FallbackProps } from 'react-error-boundary';

import {
  Button,
  CircleXIcon,
  Empty,
  EmptyDescription,
  EmptyHeadline,
  useDisclosure,
  EmptyCTAButton,
  Center,
} from '@frontend/legacy-components';

import { SupportDrawer } from 'components/support';

import { usePaneContext } from './context';

type PaneBodyFallbackProps = FallbackProps;

/**
 * Default error fallback for PaneBody. Shows a short message and a "Try again"
 * button that refetches refetchQueryKey (if provided) then resets the boundary.
 */
export const PaneBodyFallback = ({
  resetErrorBoundary,
}: PaneBodyFallbackProps) => {
  const { refetchQueryKey } = usePaneContext();
  const queryClient = useQueryClient();
  const supportDrawerDisclosure = useDisclosure();

  const handleRetry = useCallback(async () => {
    if (refetchQueryKey) {
      await queryClient.refetchQueries({ queryKey: refetchQueryKey });
    }
    resetErrorBoundary();
  }, [queryClient, refetchQueryKey, resetErrorBoundary]);

  return (
    <Center width="100%" height="100%" bg="page.background">
      <Empty size="md" py={3}>
        <CircleXIcon color="alert.error" />
        <EmptyHeadline>Unable to load</EmptyHeadline>
        <EmptyDescription>
          The data couldn&apos;t be retrieved. If the issue persists, our{' '}
          <Button
            size="md"
            variant="link"
            onClick={supportDrawerDisclosure.onOpen}
          >
            support team
          </Button>{' '}
          is ready to help.
        </EmptyDescription>
        <EmptyCTAButton onClick={handleRetry}>Retry</EmptyCTAButton>
        <SupportDrawer {...supportDrawerDisclosure} />
      </Empty>
    </Center>
  );
};
